import { Component } from '@theme/component';

/**
 * Isolated enhancement owner for opted-in editorial links and reveal targets.
 *
 * @typedef {object} ViewerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLDivElement} viewport
 * @property {HTMLDivElement} canvas
 * @property {HTMLImageElement} image
 * @property {HTMLParagraphElement} caption
 * @property {HTMLSpanElement} status
 * @property {HTMLParagraphElement} message
 * @property {HTMLAnchorElement} original
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLButtonElement} zoomIn
 * @property {HTMLButtonElement} zoomOut
 * @property {HTMLButtonElement} fit
 * @property {HTMLButtonElement} closeButton
 *
 * @extends Component<ViewerRefs>
 */
export class PoiesisEditorialInteractions extends Component {
  requiredRefs = ['dialog', 'viewport', 'canvas', 'image', 'caption', 'status', 'message', 'original', 'previous', 'next', 'zoomIn', 'zoomOut', 'fit', 'closeButton'];

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    // Identical refs retain the open session; replaced controls release old resources.
    if (this.requiredRefs.some((name) => this.boundRefs?.[name] !== this.refs[name])) this.initialize();
  }

  initialize() {
    this.teardown();
    this.boundRefs = { ...this.refs };
    for (const name of this.requiredRefs) this[name] = this.refs[name];
    this.main = document.getElementById('MainContent');
    if (!this.main || this.main.dataset.template === 'index' || !this.dialog) return;

    this.abort = new AbortController();
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.seen = new WeakSet();
    this.observed = new Set();
    this.animations = new Set();
    this.slides = [];
    this.index = 0;
    this.zoom = 1;
    this.loadToken = 0;
    const options = { signal: this.abort.signal };

    if (typeof this.dialog.showModal === 'function' && this.viewport && this.image && this.canvas && this.closeButton) {
      this.main.addEventListener('click', (event) => this.handleOpen(event), options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.cleanupSession(), options);
      this.viewport.addEventListener('pointermove', (event) => this.pointerMove(event), options);
      this.viewport.addEventListener('pointerup', (event) => this.pointerUp(event), options);
      this.viewport.addEventListener('pointercancel', () => this.clearPointer(), options);
      this.viewport.addEventListener('lostpointercapture', () => this.clearPointer(), options);
      this.viewport.addEventListener('dblclick', () => this.setZoom(this.zoom === 1 ? 2 : 1), options);
      window.addEventListener('resize', () => this.layoutImage(false), options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.layoutImage(false));
        this.resizeObserver.observe(this.viewport);
      }
    }

    this.motion.addEventListener('change', () => this.motionChanged(), options);
    document.addEventListener('shopify:section:load', (event) => this.observeReveals(event.target), options);
    document.addEventListener('shopify:section:unload', (event) => {
      if (!(event.target instanceof Element)) return;
      if (this.trigger && event.target.contains(this.trigger)) this.close();
      for (const element of this.observed) {
        if (!event.target.contains(element)) continue;
        this.revealObserver?.unobserve(element);
        this.observed.delete(element);
      }
    }, options);
    this.observeReveals(this.main);
  }

  /**
   * Preserve ordinary link behavior for modified clicks or unusable images.
   * @param {MouseEvent} event
   */
  handleOpen(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('a[data-poiesis-image]');
    const gallery = trigger?.closest('[data-poiesis-gallery]');
    if (!trigger || !gallery || !this.main.contains(gallery)) return;
    const slides = Array.from(gallery.querySelectorAll('a[data-poiesis-image]')).filter((anchor) => anchor.closest('[data-poiesis-gallery]') === gallery).map((anchor) => this.readSlide(anchor)).filter(Boolean);
    const index = slides.findIndex((slide) => slide.trigger === trigger);
    if (index < 0 || !slides.length) return;
    this.slides = slides;
    this.index = index;
    this.trigger = trigger;
    try {
      this.dialog.showModal();
    } catch {
      this.slides = [];
      this.trigger = null;
      return;
    }
    event.preventDefault();
    this.lockScroll();
    this.renderSlide();
    this.closeButton.focus({ preventScroll: true });
  }

  readSlide(anchor) {
    const image = anchor.querySelector('img');
    const href = anchor.getAttribute('href')?.trim();
    if (!image || !href || href.startsWith('#') || !(image.currentSrc || image.getAttribute('src') || image.getAttribute('srcset'))) return null;
    let url;
    try { url = new URL(href, document.baseURI); } catch { return null; }
    if (!['https:', 'http:'].includes(url.protocol)) return null;
    return {
      trigger: anchor,
      url: url.href,
      alt: image.alt || '',
      caption: anchor.dataset.poiesisCaption ?? anchor.closest('figure')?.querySelector('figcaption')?.textContent?.trim() ?? '',
      width: image.naturalWidth || Number(image.getAttribute('width')) || 1,
      height: image.naturalHeight || Number(image.getAttribute('height')) || 1,
    };
  }

  renderSlide() {
    const slide = this.slides[this.index];
    if (!slide || !this.dialog.open) return;
    this.clearPointer();
    this.zoom = 1;
    this.imageReady = false;
    this.imageFailed = false;
    const token = ++this.loadToken;
    this.intrinsicWidth = slide.width;
    this.intrinsicHeight = slide.height;
    this.image.alt = slide.alt;
    this.image.width = slide.width;
    this.image.height = slide.height;
    this.image.style.visibility = 'hidden';
    this.caption.textContent = slide.caption;
    this.original.href = slide.url;
    this.original.hidden = true;
    this.message.textContent = this.dataset.loadingText || '';
    this.message.hidden = false;
    this.updateControls();
    this.layoutImage(true);
    this.image.onload = () => {
      if (token !== this.loadToken || !this.dialog.open) return;
      this.imageReady = true;
      this.intrinsicWidth = this.image.naturalWidth || slide.width;
      this.intrinsicHeight = this.image.naturalHeight || slide.height;
      this.image.style.visibility = '';
      this.message.hidden = true;
      this.layoutImage(true);
      this.updateControls();
    };
    this.image.onerror = () => {
      if (token !== this.loadToken || !this.dialog.open) return;
      this.imageFailed = true;
      this.message.textContent = this.dataset.errorText || '';
      this.message.hidden = false;
      this.original.hidden = false;
      this.updateControls();
    };
    this.image.src = slide.url;
    if (this.image.complete && this.image.naturalWidth > 0) this.image.onload();
  }

  updateControls() {
    const count = this.slides.length;
    const singular = count < 2;
    if (this.previous) { this.previous.hidden = singular; this.previous.disabled = singular || this.index === 0; }
    if (this.next) { this.next.hidden = singular; this.next.disabled = singular || this.index >= count - 1; }
    if (this.zoomIn) this.zoomIn.disabled = !this.imageReady || this.zoom === 2;
    if (this.zoomOut) this.zoomOut.disabled = !this.imageReady || this.zoom === 1;
    if (this.fit) this.fit.disabled = !this.imageReady || this.zoom === 1;
    this.dialog.dataset.zoomed = String(this.zoom > 1);
    const countText = (this.dataset.countTemplate || '__CURRENT__ / __TOTAL__').replace('__CURRENT__', String(this.index + 1)).replace('__TOTAL__', String(count));
    const statusText = this.imageFailed ? `${countText}. ${this.dataset.errorText || ''}` : countText;
    if (this.status && this.status.textContent !== statusText) this.status.textContent = statusText;
  }

  /** @param {number} direction - Numeric data from /move/-1 or /move/1. */
  move(direction) {
    if (!this.dialog.open || this.slides.length < 2) return;
    const next = Math.max(0, Math.min(this.slides.length - 1, this.index + direction));
    if (next === this.index) return;
    this.index = next;
    this.renderSlide();
    // Boundary navigation can disable its trigger; keep keyboard shortcuts in the dialog.
    this.viewport.focus({ preventScroll: true });
  }

  /** @param {number} value - Numeric data from /setZoom/1 or /setZoom/2. */
  setZoom(value) {
    if (!this.dialog.open || !this.imageReady) return;
    const next = value > 1 ? 2 : 1;
    if (next === this.zoom) return;
    this.clearPointer();
    this.zoom = next;
    this.layoutImage(true);
    this.updateControls();
    // Zoom/Fit disable themselves after use, so transfer focus to the stable image area.
    this.viewport.focus({ preventScroll: true });
  }

  /** Image dimensions change inside the fixed viewer, never in the page layout. */
  layoutImage(center) {
    if (!this.dialog?.open || !this.viewport || !this.intrinsicWidth || !this.intrinsicHeight) return;
    const width = this.viewport.clientWidth;
    const height = this.viewport.clientHeight;
    if (!width || !height) return;
    const fit = Math.min(width / this.intrinsicWidth, height / this.intrinsicHeight, 1);
    const imageWidth = Math.max(1, Math.round(this.intrinsicWidth * fit * this.zoom));
    const imageHeight = Math.max(1, Math.round(this.intrinsicHeight * fit * this.zoom));
    const canvasWidth = Math.max(width, imageWidth);
    const canvasHeight = Math.max(height, imageHeight);
    this.canvas.style.width = `${canvasWidth}px`;
    this.canvas.style.height = `${canvasHeight}px`;
    this.image.style.width = `${imageWidth}px`;
    this.image.style.height = `${imageHeight}px`;
    if (center) this.viewport.scrollTo({ left: (canvasWidth - width) / 2, top: (canvasHeight - height) / 2, behavior: 'instant' });
  }

  /** @param {KeyboardEvent} event */
  handleKey(event) {
    if (!this.dialog.open || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'Tab') {
      // Recompute after each zoom/slide change: hidden or disabled controls are not stops.
      const focusable = Array.from(this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')).filter((element) =>
        element instanceof HTMLElement && element.tabIndex >= 0 && !element.disabled && !element.closest('[hidden]') && element.getClientRects().length > 0
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!first) {
        event.preventDefault();
        this.viewport.focus({ preventScroll: true });
      } else if (!focusable.includes(active) || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      }
      return;
    }
    const action = {
      ArrowLeft: () => this.move(-1),
      ArrowRight: () => this.move(1),
      '+': () => this.setZoom(2),
      '=': () => this.setZoom(2),
      '-': () => this.setZoom(1),
      '0': () => this.setZoom(1),
      Escape: () => this.close(),
    }[event.key];
    if (!action) return;
    event.preventDefault();
    action();
  }

  /** @param {PointerEvent} event */
  pointerDown(event) {
    if (!event.isPrimary || event.button !== 0) { this.clearPointer(); return; }
    this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, top: this.viewport.scrollTop, touch: event.pointerType === 'touch' };
    if (this.zoom > 1 && event.pointerType !== 'touch') {
      this.viewport.setPointerCapture(event.pointerId);
      this.dialog.dataset.dragging = '';
      event.preventDefault();
    }
  }

  pointerMove(event) {
    if (!this.pointer || this.pointer.id !== event.pointerId || this.zoom === 1 || this.pointer.touch) return;
    this.viewport.scrollLeft = this.pointer.left - (event.clientX - this.pointer.x);
    this.viewport.scrollTop = this.pointer.top - (event.clientY - this.pointer.y);
    event.preventDefault();
  }

  pointerUp(event) {
    const pointer = this.pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    const dx = event.clientX - pointer.x;
    const dy = event.clientY - pointer.y;
    const swipe = this.zoom === 1 && pointer.touch && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5;
    this.clearPointer();
    if (swipe) this.move(dx < 0 ? 1 : -1);
  }

  clearPointer() {
    const id = this.pointer?.id;
    this.pointer = null;
    if (this.dialog) delete this.dialog.dataset.dragging;
    if (id !== undefined && this.viewport?.hasPointerCapture(id)) this.viewport.releasePointerCapture(id);
  }

  lockScroll() {
    if (this.scrollLock) return;
    const style = document.documentElement.style;
    this.scrollLock = { overflow: style.overflow, gutter: style.scrollbarGutter };
    style.scrollbarGutter = 'stable';
    style.overflow = 'hidden';
  }

  /** Public close action; the declarative click event requires no additional data. */
  close() { if (this.dialog?.open) this.dialog.close(); }

  cleanupSession() {
    this.loadToken = (this.loadToken || 0) + 1;
    this.clearPointer();
    if (this.image) { this.image.onload = null; this.image.onerror = null; this.image.removeAttribute('src'); }
    if (this.scrollLock) {
      const style = document.documentElement.style;
      if (style.overflow === 'hidden') style.overflow = this.scrollLock.overflow;
      if (style.scrollbarGutter === 'stable') style.scrollbarGutter = this.scrollLock.gutter;
      this.scrollLock = null;
    }
    const trigger = this.trigger;
    this.trigger = null;
    this.slides = [];
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    else if (trigger && this.main?.isConnected) {
      const temporaryTabindex = !this.main.hasAttribute('tabindex');
      if (temporaryTabindex) this.main.setAttribute('tabindex', '-1');
      this.main.focus({ preventScroll: true });
      if (temporaryTabindex) this.main.removeAttribute('tabindex');
    }
  }

  observeReveals(root) {
    if (this.motion.matches || !('IntersectionObserver' in window) || !(root instanceof Element)) return;
    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.revealObserver.unobserve(entry.target);
          this.observed.delete(entry.target);
          this.reveal(entry.target);
        }
      }, { threshold: .12, rootMargin: '0px 0px -24px 0px' });
    }
    const targets = [...root.querySelectorAll('[data-poiesis-reveal]')];
    if (root.matches('[data-poiesis-reveal]')) targets.unshift(root);
    for (const target of targets) {
      if (!this.main.contains(target) || this.seen.has(target) || this.observed.has(target)) continue;
      this.observed.add(target);
      this.revealObserver.observe(target);
    }
  }

  reveal(element) {
    this.seen.add(element);
    if (this.motion.matches || !element.isConnected || typeof element.animate !== 'function') return;
    if (getComputedStyle(element).transform !== 'none') return;
    try {
      const animation = element.animate([
        { opacity: .85, transform: 'translateY(12px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ], { duration: 520, easing: 'cubic-bezier(.22,.61,.36,1)', iterations: 1 });
      this.animations.add(animation);
      animation.finished.catch(() => {}).finally(() => this.animations.delete(animation));
    } catch { /* Static content remains fully visible if enhancement is unavailable. */ }
  }

  motionChanged() {
    if (this.motion.matches) {
      this.revealObserver?.disconnect();
      this.revealObserver = null;
      this.observed.clear();
      for (const animation of this.animations) animation.cancel();
      this.animations.clear();
    } else this.observeReveals(this.main);
  }

  teardown() {
    this.abort?.abort();
    this.revealObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.revealObserver = null;
    this.resizeObserver = null;
    if (this.animations) { for (const animation of this.animations) animation.cancel(); this.animations.clear(); }
    if (this.dialog?.open) this.dialog.close();
    this.cleanupSession();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.teardown();
  }
}

if (!customElements.get('poiesis-editorial-interactions')) {
  customElements.define('poiesis-editorial-interactions', PoiesisEditorialInteractions);
}
